import { Icon } from '@material-ui/core'
import { USER_TYPE } from 'app/ducks/auth/types'
import { modalFormOperations } from 'app/ducks/modal'
import { Store } from 'app/redux/Store'
import React from 'react'
import School from './school/School'
import { TENANT_FLAG } from 'app/tenantFeatureFlags'
import {
  getFeatureFlag,
  timestamp as featureFlagsTimestamp,
} from 'app/ducks/featureFlags/selectors'
import {
  INTERNAL_CAMPAIGNS_FLAG,
  INTERNAL_CAMPAIGNS_PATH,
  INTERNAL_CAMPAIGN_ROLES,
} from 'app/ducks/internalCampaigns/constants'
import {
  MCP_AUTHORIZE_PATH,
  MCP_SETUP_PATH,
  MCP_SETUP_ROLES,
  MCP_USAGE_PATH,
  MCP_USAGE_ROLES,
} from './mcp/mcp'
import {
  CANCELLATION_REPORT_PATH,
  RETENTION_ROLES,
} from './retention/constants'
import { retentionModuleUserCondition } from './retention/hooks'
import {
  BENEFIT_POINTS_DASHBOARD_FLAG,
  BENEFIT_POINTS_DASHBOARD_PATH,
} from './benefit-points/benefitPoints'
import { SURVEY_MANAGEMENT_FLAG } from './survey/surveyManagementFlag'
import { SURVEY_BUILDER_PATH } from './survey/builder/paths'
import { ATTENDANCE_RANKING_FLAG } from './attendance/attendanceRankingFlag'

const ComingSoon = React.lazy(() => import('./users/comingSoon'))

export const signUp = {
  path: '/session/signup',
  component: React.lazy(() =>
    import('app/views/sessions/register/JwtRegister')
  ),
  isPublic: true,
}

export const signIn = {
  path: '/session/signin',
  component: React.lazy(() => import('app/views/sessions/login/JwtLogin')),
  isPublic: true,
}

export const biometryEnroll = {
  name: 'Cadastro de biometria',
  icon: 'fingerprint',
  path: '/biometry-enroll',
  featureFlag: TENANT_FLAG.PHYSICAL_ACCESS_CONTROL,
  component: React.lazy(() =>
    import('app/views/biometry-enroll/BiometryEnroll')
  ),
  roles: [USER_TYPE.BIOMETRY_MANAGER],
}

export const physicalSpaceReservations = {
  name: 'Reservas de espaço',
  icon: 'deck',
  path: '/physical-space-reservations',
  featureFlag: TENANT_FLAG.PHYSICAL_SPACE_RESERVATIONS,
  component: React.lazy(() =>
    import('app/views/physical-space-reservations/PhysicalSpaceReservations')
  ),
  roles: [
    USER_TYPE.BIOMETRY_MANAGER,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const physicalAccess = {
  name: 'Acesso Físico',
  icon: 'arrow_circle_up',
  path: '/physical-access',
  featureFlag: TENANT_FLAG.PHYSICAL_ACCESS_CONTROL,
  component: React.lazy(() =>
    import('app/views/physical-access/PhysicalAccess')
  ),
  roles: [
    USER_TYPE.COLLABORATOR,
    USER_TYPE.BIOMETRY_MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const accountActivation = {
  path: '/session/account-activation',
  component: React.lazy(() =>
    import('app/views/sessions/activation/AccountActivation')
  ),
  isPublic: true,
}

export const forgotPassword = {
  path: '/session/forgot-password',
  component: React.lazy(() => import('app/views/sessions/ForgotPassword')),
  isPublic: true,
}

export const notFound = {
  path: '/session/404',
  component: React.lazy(() => import('app/views/sessions/NotFound')),
  isPublic: true,
}

export const servicesUnavailable = {
  path: '/services-unavailable',
  component: React.lazy(() => import('app/views/sessions/ServiceUnavailable')),
  isPublic: true,
}

export const dashboard = {
  name: 'Dashboard',
  icon: 'dashboard',
  path: '/dashboard',
  component: React.lazy(() => import('app/views/dashboard/Dashboard')),
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const userDetails = {
  path: '/user/details/:id',
  exact: true,
  component: React.lazy(() =>
    import('app/views/users/UserDetails/UserDetails')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.MANAGER, USER_TYPE.COLLABORATOR],
  capability: 'view_user',
}

export const userList = {
  name: 'Listagem de usuários',
  icon: 'people',
  iconText: 'SI',
  path: '/user/list',
  exact: true,
  component: React.lazy(() => import('app/views/users/UserList')),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.MANAGER, USER_TYPE.COLLABORATOR],
  capability: 'view_user',
}

export const userForm = {
  path: '/user/form',
  exact: true,
  component: React.lazy(() => import('app/views/users/UserForm')),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.MANAGER, USER_TYPE.COLLABORATOR],
  capability: 'change_user_records',
}

export const userMergeSuggestions = {
  name: 'Merge de usuários',
  icon: 'merge_type',
  path: '/user/merge-suggestions',
  exact: true,
  component: React.lazy(() => import('app/views/users/MergeSuggestions')),
  roles: [USER_TYPE.ADMINISTRATOR],
}

export const dataSharingRequests = {
  name: 'Compartilhamento entre tenants',
  icon: 'sync_alt',
  path: '/user/data-sharing-requests',
  exact: true,
  component: React.lazy(() => import('app/views/users/DataSharingRequests')),
  roles: [USER_TYPE.ADMINISTRATOR],
}

export const finances = {
  name: 'Finanças',
  icon: 'attach_money',
  path: '/finances',
  exact: true,
  component: React.lazy(() => import('app/views/finances/FinancesDashboard')),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  featureFlag: TENANT_FLAG.FINANCES_MODULE,
}

export const billingPage = {
  name: 'Cobrança Manual',
  icon: 'attach_money',
  path: '/billing',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/BillingPage/BillingPage')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  featureFlag: TENANT_FLAG.FINANCES_MODULE,
}

export const accountsPayablePage = {
  name: 'Contas a pagar',
  icon: 'attach_money',
  path: '/accounts-payable',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/AccountsPayablePage/AccountsPayablePage')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  featureFlag: TENANT_FLAG.ACCOUNTS_PAYABLE_RECEIVABLE,
}

export const accountsReceivablePage = {
  name: 'Contas a receber',
  icon: 'attach_money',
  path: '/accounts-receivable',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/AccountsReceivablePage/AccountsReceivablePage')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  featureFlag: TENANT_FLAG.ACCOUNTS_PAYABLE_RECEIVABLE,
}

export const financialDocumentsList = {
  name: 'Documentos Financeiros',
  icon: 'attach_money',
  path: '/financial-documents',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/FinancialDocumentsList')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  featureFlag: TENANT_FLAG.FINANCIAL_DOCUMENTS_LIST,
}

export const calendar = {
  name: 'Calendário',
  icon: 'perm_contact_calendar',
  path: '/calendar',
  exact: true,
  component: React.lazy(() => import('app/views/calendar/Calendar')),
  roles: [
    USER_TYPE.ADMINISTRATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.FINANCIAL,
    USER_TYPE.BIOMETRY_MANAGER,
  ],
  capability: 'view_calendar_module',
  featureFlag: TENANT_FLAG.CALENDAR,
}

export const associationTitles = {
  name: 'Associados',
  icon: 'person',
  path: '/associados',
  exact: true,
  component: React.lazy(() =>
    import('app/views/association-title/AssociationTitleList')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
  featureFlag: TENANT_FLAG.ASSOCIATION_TITLES,
}

export const notificationAnalytics = {
  name: 'Analytics de Notificações',
  icon: 'query_stats',
  path: '/notification-analytics',
  exact: true,
  component: React.lazy(() =>
    import('app/views/notifications/NotificationAnalytics')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
}

export const financialMovementsForecasting = {
  name: 'Previsão de movimentos financeiros',
  icon: 'attach_money',
  path: '/financial-movements-forecasting',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/FinancialMovementsForecasting')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
}

export const financialDocumentsForecasting = {
  name: 'Previsão de documentos financeiros',
  icon: 'attach_money',
  path: '/financial-documents-forecasting',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/FinancialDocumentsForecasting')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
}

export const paymentMethodsSync = {
  name: 'Sincronização de métodos de pagamento',
  icon: 'attach_money',
  path: '/payment-methods-sync',
  exact: true,
  component: React.lazy(() => import('app/views/finances/PaymentMethodsSync')),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  featureFlag: TENANT_FLAG.PAYMENT_METHODS_SYNC,
}

export const benefits = {
  name: 'Programa de benefícios',
  icon: 'loyalty',
  path: '/benefits',
  exact: true,
  featureFlag: TENANT_FLAG.BENEFITS,
  component: () => <ComingSoon image="/assets/images/benefits.svg" />,
  badge: {
    color: 'error',
    value: 'Em breve!',
  },
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const gym = {
  name: 'Academia',
  icon: 'sports_handball',
  path: '/gym',
  exact: true,
  component: React.lazy(() => import('app/views/gym/PersonalDashboard')),
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.TOTEM,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.BIOMETRY_MANAGER, // TODO: Filter based on access type instead of role
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
  capability: 'view_gym_module',
  featureFlag: TENANT_FLAG.GYM_MODULE,
}

export const gymTeacherDashboard = {
  name: 'Gestão da Academia',
  icon: 'groups',
  path: '/gym/manage',
  exact: true,
  component: React.lazy(() => import('app/views/gym/TeacherDashboard')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  capability: 'view_gym_staff_module',
  featureFlag: TENANT_FLAG.GYM_TEACHER_DASHBOARD,
}

export const gymAccessDashboard = {
  name: 'Acessos à Academia',
  icon: 'sports_handball',
  path: '/gym-access',
  exact: true,
  component: React.lazy(() => import('app/views/gym/GymAccessDashboard')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  featureFlag: TENANT_FLAG.GYM_ACCESS_DASHBOARD,
}

export const contracts = {
  name: 'Modelos de Contrato',
  icon: 'article',
  path: '/contracts',
  exact: true,
  component: React.lazy(() => import('app/views/contracts/ContractsDashboard')),
  roles: [USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  featureFlag: TENANT_FLAG.CONTRACTS_MODULE,
}

export const contractsCrm = {
  name: 'CRM de Contratos',
  icon: 'article',
  path: '/contracts-crm',
  exact: true,
  component: React.lazy(() => import('app/views/contracts/ContractsCRM')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  capability: 'view_contracts_crm_module',
  featureFlag: TENANT_FLAG.CONTRACTS_CRM,
}

// Retention module (proposal P2), gated by the `retention_module` feature flag
// (seed migration: amsm-backend
// hall/feature_flags/migrations/0010_seed_retention_module_flag.py). While
// flags are still loading the route is allowed and the page redirects to 404
// itself once they resolve to off (see retentionModuleUserCondition).
export const cancellationReport = {
  name: 'Relatório de cancelamentos',
  icon: 'event_busy',
  path: CANCELLATION_REPORT_PATH,
  exact: true,
  component: React.lazy(() => import('app/views/retention/CancellationReport')),
  roles: RETENTION_ROLES,
  capability: 'view_contracts_crm_module',
  userCondition: retentionModuleUserCondition,
  featureFlag: TENANT_FLAG.CONTRACTS_CRM,
}

export const campaigns = {
  name: 'Campanhas',
  icon: 'campaigns',
  path: '/campaigns',
  exact: true,
  component: React.lazy(() => import('app/views/campaigns/Campaigns')),
  roles: [USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  featureFlag: TENANT_FLAG.CAMPAIGNS,
}

export const findPhysicalSpaces = {
  name: 'Espaços Físicos',
  icon: 'deck',
  path: '/find-physical-spaces',
  exact: true,
  component: React.lazy(() =>
    import('app/views/physical-spaces/FindPhysicalSpaces')
  ),
  featureFlag: TENANT_FLAG.PHYSICAL_SPACE_RESERVATIONS,
  // TODO: Keep minimum age condition for amsm
  // minimumAge: 18,
  userCondition: (userInfo, featureFlags) =>
    featureFlags?.ENABLE_SPACE_RESERVATION ||
    userInfo?.is_physical_space_reservation_staff ||
    [USER_TYPE.BIOMETRY_MANAGER, USER_TYPE.ADMINISTRATOR].includes(
      userInfo?.user_type
    ),
}

export const physicalSpaceDetails = {
  name: 'Espaços Físicos',
  icon: 'deck',
  path: '/physical-space/:id',
  exact: true,
  component: React.lazy(() =>
    import('app/views/physical-spaces/PhysicalSpaceDetails')
  ),
  roles: [
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.BIOMETRY_MANAGER,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const physicalEvaluationForm = {
  name: 'Academia',
  icon: 'sports_handball',
  path: '/physical-evaluation-form/:id?',
  exact: true,
  component: React.lazy(() => import('app/views/gym/PhysicalEvaluationForm')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

export const workouts = {
  name: 'Treinos',
  icon: 'sports_handball',
  path: '/workouts',
  exact: true,
  component: React.lazy(() => import('app/views/gym/Workouts')),
  roles: [
    USER_TYPE.TOTEM,
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}
export const findClasses = {
  name: 'Encontrar aulas',
  icon: 'search',
  path: '/find-classes',
  exact: true,
  component: React.lazy(() => import('app/views/gym/FindClasses')),
  roles: [
    USER_TYPE.TOTEM,
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}
export const activityClassesDetails = {
  name: 'Detalhes da aula',
  icon: 'school',
  path: '/activity-class-details/:id',
  exact: true,
  component: React.lazy(() => import('app/views/gym/ActivityClassDetails')),
  roles: [
    USER_TYPE.TOTEM,
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const workoutsDetails = {
  name: 'Treino',
  icon: 'sports_handball',
  path: '/workout/:id',
  exact: true,
  component: React.lazy(() => import('app/views/gym/WorkoutDetails')),
  roles: [
    USER_TYPE.TOTEM,
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],

  // bottomBar: {
  //   centerButton: {
  //     icon: <Icon>printer</Icon>,
  //     label: 'Imprimir',
  //   },
  // },
}

export const workoutPlayer = {
  name: 'Player de Treino',
  icon: 'play_arrow',
  path: '/workout-player/:id',
  exact: true,
  component: React.lazy(() => import('app/views/gym/WorkoutPlayer')),
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const workoutForm = {
  name: 'Formulário de Treino',
  icon: 'sports_handball',
  path: '/workout-form/:id?',
  exact: true,
  component: React.lazy(() => import('app/views/gym/WorkoutForm')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}
export const activityclassform = {
  name: 'Formulário de Aula',
  icon: 'sports_handball',
  path: '/activity-class/:id?',
  exact: true,
  component: React.lazy(() => import('app/views/gym/ActivityClassForm')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

export const evaluations = {
  name: 'Avaliações',
  icon: 'sports_handball',
  path: '/evaluations',
  exact: true,
  component: React.lazy(() => import('app/views/gym/Evaluations')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

export const schools = {
  name: 'Modalidades',
  icon: 'sports_soccer',
  path: '/schools',
  exact: true,
  featureFlag: TENANT_FLAG.SCHOOLS,
  component: () => <School />,
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

// Attendance ranking and class occupancy, from the in-app roll call. Gated by
// the `attendance_ranking` feature flag (see docs/FEATURE_FLAGS.md; seed
// migration: amsm-backend
// hall/feature_flags/migrations/0014_seed_attendance_ranking_flag.py). Like
// `internalCampaigns`, it's allowed while flags are still loading (they're
// cleared on every page load) so a reload doesn't 404; the page redirects to
// 404 itself once they resolve to off.
export const attendanceReport = {
  name: 'Frequência',
  icon: 'fact_check',
  path: '/attendance-report',
  exact: true,
  featureFlag: TENANT_FLAG.SCHOOLS,
  component: React.lazy(() => import('./attendance/AttendanceReport')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  userCondition: () => {
    const state = Store.getState()
    return (
      !featureFlagsTimestamp(state) ||
      getFeatureFlag(state, ATTENDANCE_RANKING_FLAG)
    )
  },
}

export const spaceRenting = {
  name: 'Aluguel de espaços',
  icon: 'house_siding',
  path: '/space-renting',
  exact: true,
  featureFlag: TENANT_FLAG.SPACE_RENTING,
  component: React.lazy(() =>
    import('app/views/physical-space-renting/PhysicalSpaceRenting')
  ),
  userCondition: (userInfo, featureFlags) =>
    featureFlags?.ENABLE_SPACE_RESERVATION ||
    userInfo?.is_physical_space_reservation_staff ||
    [USER_TYPE.BIOMETRY_MANAGER, USER_TYPE.ADMINISTRATOR].includes(
      userInfo?.user_type
    ),
}

export const events = {
  name: 'Eventos',
  icon: 'celebration',
  path: '/events',
  exact: true,
  featureFlag: TENANT_FLAG.EVENTS,
  component: React.lazy(() => import('app/views/events/Events')),
}

export const tickets = {
  name: 'Cobranças',
  icon: 'local_atm',
  path: '/user-financial-documents',
  exact: true,
  featureFlag: TENANT_FLAG.USER_FINANCIAL_DOCUMENTS,
  component: React.lazy(() =>
    import('./user-financial-documents/UserFinancialDocuments')
  ),
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const customReport = {
  name: 'Relatórios',
  icon: 'content_paste_search_icon',
  path: '/custom-report',
  exact: true,
  featureFlag: TENANT_FLAG.CUSTOM_REPORT,
  component: React.lazy(() => import('./custom-report/CustomReport')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  capability: 'view_reports_module',
}

export const userPersonalData = {
  path: '/personal-data',
  exact: true,
  component: React.lazy(() => import('./users/UserPersonalData')),
  badge: {
    color: 'error',
    value: 'Em breve!',
  },
  bottomBar: {
    rightButton: {
      label: 'Editar',
      icon: <Icon>create</Icon>,
      onClick: () => {
        const state = Store.getState()
        Store.dispatch(
          modalFormOperations.showModalForm({
            app: 'users',
            model: 'user',
            id: state.auth?.userInfo?.id,
          })
        )
      },
    },
  },
}

export const physicalDeviceManagement = {
  name: 'Controle de acesso',
  icon: 'login',
  path: '/physical-device-management',
  exact: true,
  component: React.lazy(() =>
    import('./physical-device-management/PhysicalDeviceManagement')
  ),
  featureFlag: TENANT_FLAG.PHYSICAL_ACCESS_CONTROL,
  roles: [USER_TYPE.ADMINISTRATOR],
}

// Survey builder - gated by the `survey_management` feature flag (see
// docs/FEATURE_FLAGS.md; seed migration: amsm-backend
// hall/feature_flags/migrations/0013_seed_survey_management_flag.py).
// Like `internalCampaigns`, flags are re-fetched on every page load, so the
// route stays reachable while they're pending and the pages redirect to 404
// themselves once the flag resolves to off.
const isSurveyManagementAllowed = () => {
  const state = Store.getState()
  return (
    !featureFlagsTimestamp(state) ||
    getFeatureFlag(state, SURVEY_MANAGEMENT_FLAG)
  )
}

export const surveyBuilder = {
  name: 'Questionários',
  icon: 'assignment',
  path: SURVEY_BUILDER_PATH,
  exact: true,
  component: React.lazy(() => import('./survey/builder/SurveyBuilderList')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  userCondition: isSurveyManagementAllowed,
}

export const surveyBuilderEditor = {
  name: 'Questionário',
  icon: 'assignment',
  path: `${SURVEY_BUILDER_PATH}/:id`,
  exact: true,
  component: React.lazy(() => import('./survey/builder/SurveyBuilderEditor')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  userCondition: isSurveyManagementAllowed,
}

// Per-survey insights (charts + respondents), reached from the survey list
// and editor. Same flag and roles as the builder.
export const surveyInsights = {
  name: 'Insights do questionário',
  icon: 'insights',
  path: `${SURVEY_BUILDER_PATH}/:id/insights`,
  exact: true,
  component: React.lazy(() => import('./survey/insights/SurveyInsights')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  userCondition: isSurveyManagementAllowed,
}

export const totem = {
  name: 'TotemDisplay',
  path: '/totem-display',
  exact: true,
  component: React.lazy(() => import('./totem/TotemDisplay')),
  roles: [USER_TYPE.TOTEM, USER_TYPE.ADMINISTRATOR],
  hideLayout: true,
}

export const healthInsuranceComparisonReport = {
  name: 'Planos de Saúde',
  icon: 'health_and_safety',
  path: '/health-insurance-comparison-report',
  exact: true,
  component: React.lazy(() =>
    import('app/views/health-insurance/HealthInsuranceComparisonReport')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
  featureFlag: TENANT_FLAG.HEALTH_AND_LIFE_INSURANCE,
}

export const lifeInsuranceComparisonReport = {
  name: 'Seguros de Vida',
  icon: 'health_and_safety',
  path: '/life-insurance-comparison-report',
  exact: true,
  component: React.lazy(() =>
    import('app/views/life-insurance/LifeInsuranceComparisonReport')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
  featureFlag: TENANT_FLAG.HEALTH_AND_LIFE_INSURANCE,
}

export const physicalEvaluationHistory = {
  name: 'Planos de Saúde',
  icon: 'health_and_safety',
  path: '/gym/physical-evaluation-history',
  exact: true,
  component: React.lazy(() =>
    import('app/views/gym/PhysicalEvaluationHistory')
  ),
  isPublic: false,
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const healthSignalAnalysis = {
  name: 'Análise de Sinais Vitais',
  icon: 'monitor_heart',
  path: '/health-signals/analysis',
  exact: true,
  component: React.lazy(() => import('app/views/health/HealthSignalAnalysis')),
  isPublic: false,
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

// export const customDashboard = {
//   path: '/custom-dashboard/:slug',
//   exact: true,
//   component: React.lazy(() => import('app/views/CustomDashboard')),
//   isPublic: false,
// }

export const analyticsDashboard = {
  name: 'Analytics',
  icon: 'insights',
  path: '/analytics',
  exact: true,
  component: React.lazy(() => import('app/views/analytics/AnalyticsDashboard')),
  roles: [USER_TYPE.ADMINISTRATOR],
}

export const aiUsageDashboard = {
  name: 'Uso de IA',
  icon: 'smart_toy',
  path: '/ai-usage',
  exact: true,
  component: React.lazy(() => import('app/views/ai-usage/AIUsageDashboard')),
  roles: [USER_TYPE.ADMINISTRATOR],
}

// Administrators' benefit points dashboard, behind the
// `benefit_points_dashboard` flag (see ./benefit-points/benefitPoints.js).
// Flags are cleared on every page load and re-fetched right after, so a
// reload here would otherwise 404 before they arrive: while they're pending
// the page renders its own loading state and redirects to 404 itself once
// they resolve to off. The menu entries use `benefitPointsDashboardNav`
// instead, which stays hidden until the flag is known to be on.
export const benefitPointsDashboard = {
  name: 'Pontos de benefício',
  icon: 'loyalty',
  path: BENEFIT_POINTS_DASHBOARD_PATH,
  exact: true,
  component: React.lazy(() =>
    import('app/views/benefit-points/BenefitPointsDashboard')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
  featureFlag: TENANT_FLAG.BENEFIT_POINTS,
  userCondition: () => {
    const state = Store.getState()
    return (
      !featureFlagsTimestamp(state) ||
      getFeatureFlag(state, BENEFIT_POINTS_DASHBOARD_FLAG)
    )
  },
}

// "Conectar assistente de IA" (MCP) — the setup page and the OAuth consent page.
// Neither uses `userCondition` for the `mcp_server_access` flag: flags load
// asynchronously, so a hard reload would bounce to /session/404 before they
// arrive. The backend enforces the flag instead (404 on every
// /backend/mcp/api/* endpoint when off) and both pages render that as "not
// available"; the menu entries pointing here check the flag themselves.
export const mcpSetup = {
  name: 'Conectar assistente de IA',
  icon: 'smart_toy',
  path: MCP_SETUP_PATH,
  exact: true,
  component: React.lazy(() => import('app/views/mcp/McpSetup')),
  roles: MCP_SETUP_ROLES,
}

export const mcpUsageDashboard = {
  name: 'Uso do MCP',
  icon: 'query_stats',
  path: MCP_USAGE_PATH,
  exact: true,
  component: React.lazy(() => import('app/views/mcp-usage/McpUsageDashboard')),
  roles: MCP_USAGE_ROLES,
}

// Public on purpose: the AI assistant sends users here straight from outside the app.
// The page itself sends anyone logged out to the login and brings them back
// (app/reusable/postLoginRedirect.js).
export const mcpAuthorize = {
  path: MCP_AUTHORIZE_PATH,
  exact: true,
  component: React.lazy(() => import('app/views/mcp/McpAuthorize')),
  isPublic: true,
  hideLayout: true,
}

export const tasksBoard = {
  name: 'Quadro de Tarefas',
  icon: 'assignment',
  path: '/tasks-board',
  exact: true,
  component: React.lazy(() => import('app/views/management/TasksBoard')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  capability: 'view_tasks_board_module',
}

// Nutrition module - phase 0/1 (MVP logging). Gated by the `nutrition_module`
// feature flag (see docs/FEATURE_FLAGS.md; seed migration:
// amsm-backend hall/feature_flags/migrations/0004_seed_nutrition_module_flag.py) -
// off for every tenant until turned on, starting with AMSM as the pilot.
// `userCondition` is checked both for route access (`AppRoute` in App.jsx)
// and nav visibility (`shouldRenderNavigation`, used by both the legacy
// `navigations.js` sidenav and the new `sections.js` section rail), so
// gating here covers every nav surface with one flag check.
const NUTRITION_ROLES = [
  USER_TYPE.MEMBER,
  USER_TYPE.RELATIVE,
  USER_TYPE.RESPONSIBLE,
  USER_TYPE.ASSOCIATE,
  USER_TYPE.DIRECTOR,
  USER_TYPE.COLLABORATOR,
  USER_TYPE.MANAGER,
  USER_TYPE.ADMINISTRATOR,
]

const isNutritionModuleEnabled = () =>
  getFeatureFlag(Store.getState(), 'nutrition_module')

export const nutritionDiary = {
  name: 'Nutrição',
  icon: 'restaurant',
  path: '/nutrition/diary',
  exact: true,
  component: React.lazy(() => import('app/views/nutrition/NutritionDiary')),
  roles: NUTRITION_ROLES,
  userCondition: isNutritionModuleEnabled,
}

export const nutritionProfileForm = {
  name: 'Perfil Nutricional',
  icon: 'restaurant',
  path: '/nutrition/profile/:id?',
  exact: true,
  component: React.lazy(() =>
    import('app/views/nutrition/NutritionProfileForm')
  ),
  roles: NUTRITION_ROLES,
  userCondition: isNutritionModuleEnabled,
}

// Reached from the top-bar "Novidades" button (InternalCampaignsButton), not
// from the section/side navigation.
export const internalCampaigns = {
  name: 'Novidades',
  icon: 'auto_awesome',
  path: INTERNAL_CAMPAIGNS_PATH,
  exact: true,
  component: React.lazy(() =>
    import('app/views/internalCampaigns/InternalCampaigns')
  ),
  roles: INTERNAL_CAMPAIGN_ROLES,
  // Feature flags are cleared on every page load and re-fetched right
  // after, so a reload on /novidades would otherwise 404 before they
  // arrive. While they're pending the page renders its own loading state
  // and redirects to 404 itself once they resolve to off.
  userCondition: () => {
    const state = Store.getState()
    return (
      !featureFlagsTimestamp(state) ||
      getFeatureFlag(state, INTERNAL_CAMPAIGNS_FLAG)
    )
  },
}
